/**
 * The chart — task P2-05.
 *
 * DESIGN §8.2, "keep the door open": this component is the ONLY place that
 * knows ECharts exists. Everything above it speaks the props interface below —
 * `{ series, events, range, onRangeChange }` — so swapping renderers touches
 * one file rather than the app. The design calls that decision reversible and
 * says not to spend long on it; this is what keeps it reversible.
 *
 * Two rendering choices carry real meaning and should not be "tidied":
 *
 *   - MIN/MAX IS DRAWN AS A BAND, not just the mean line. At the 1-month view a
 *     three-second crank dip is invisible in the mean and unmistakable in
 *     `mvMin`. The band is the entire reason a user can still see events when
 *     zoomed out (design §8.3). Removing it would make the chart look cleaner
 *     and hide the thing it exists to show.
 *
 *   - UNPLACED REGIONS ARE DRAWN, in a visually distinct hatch. These are
 *     stretches where the device holds data it could not place on a timeline.
 *     Showing nothing there would tell the user "no data", which is false.
 */
import { LineChart } from "echarts/charts";
import {
    DataZoomComponent,
    GridComponent,
    LegendComponent,
    MarkAreaComponent,
    MarkLineComponent,
    TooltipComponent,
} from "echarts/components";
import * as echarts from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import ReactEChartsCore from "echarts-for-react/lib/core";
import { useMemo, useRef } from "react";

/**
 * Register only what this chart draws.
 *
 * `import ReactECharts from "echarts-for-react"` pulls in every chart type,
 * renderer, and component ECharts ships — around 450 KB gzipped, against the
 * 150–250 KB the design budgets for a tree-shaken build (§8.2). Importing from
 * `echarts/core` costs this registration block and saves most of that.
 *
 * Adding a feature to the chart above means adding its component here, and the
 * symptom of forgetting is that the feature silently does not render.
 */
echarts.use([
    LineChart,
    GridComponent,
    TooltipComponent,
    LegendComponent,
    DataZoomComponent,
    MarkAreaComponent,
    MarkLineComponent,
    CanvasRenderer,
]);

export type ChartSeriesPoint = {
    t: number;
    mvMin: number;
    mvMax: number;
    mvMean: number;
    tempMin: number | null;
    tempMax: number | null;
    tempMean: number | null;
    n: number;
};

/** Ranges the device has data for but could not position in time. */
export type ChartUnplaced = { from: number; to: number };

/** Point events overlaid on the chart. Populated from Phase 4 onward. */
export type ChartEvent = {
    id: string;
    at: number;
    endAt?: number;
    kind: string;
    severity: number;
};

export type ChartRange = { from: number; to: number };

export type VoltageChartProps = {
    series: ChartSeriesPoint[];
    events: ChartEvent[];
    range: ChartRange;
    onRangeChange: (range: ChartRange) => void;
    unplaced?: ChartUnplaced[];
    loading?: boolean;
};

const mv = (millivolts: number): number => millivolts / 1000;

export function VoltageChart(props: VoltageChartProps) {
    const { series, events, range, onRangeChange, unplaced = [], loading } = props;

    // ECharts fires `dataZoom` continuously while dragging. Committing every
    // frame would issue a request per pixel; the parent debounces, but holding
    // the latest value here keeps the callback identity stable.
    const pendingRange = useRef<ChartRange | null>(null);

    const option = useMemo(() => {
        const times = series.map((point) => point.t);

        // The band is drawn as a transparent baseline plus a stacked height,
        // which is ECharts' documented way to fill between two lines without a
        // custom series.
        const bandBase = series.map((point) => mv(point.mvMin));
        const bandHeight = series.map((point) => mv(point.mvMax - point.mvMin));
        const meanLine = series.map((point) => mv(point.mvMean));
        const tempLine = series.map((point) =>
            point.tempMean === null ? null : point.tempMean,
        );

        return {
            animation: false,
            grid: { left: 56, right: 56, top: 28, bottom: 72 },
            tooltip: {
                trigger: "axis",
                axisPointer: { type: "cross" },
                formatter: (params: unknown) => {
                    const rows = params as { dataIndex: number }[];
                    const point = series[rows[0]?.dataIndex ?? 0];
                    if (!point) return "";
                    const when = new Date(point.t).toLocaleString();
                    const temperature =
                        point.tempMean === null
                            ? "no reading"
                            : `${point.tempMean.toFixed(1)} °F` +
                              (point.tempMin !== point.tempMax
                                  ? ` (${point.tempMin}–${point.tempMax})`
                                  : "");
                    return [
                        `<strong>${when}</strong>`,
                        `mean ${mv(point.mvMean).toFixed(3)} V`,
                        `min ${mv(point.mvMin).toFixed(3)} V · max ${mv(point.mvMax).toFixed(3)} V`,
                        `temp ${temperature}`,
                        // Sample count makes the tier visible without naming
                        // it: 1 sample is a raw entry, thousands is a bucket.
                        `${point.n.toLocaleString()} samples`,
                    ].join("<br/>");
                },
            },
            legend: {
                data: ["mean", "min–max", "temperature"],
                bottom: 0,
            },
            xAxis: {
                type: "time",
                min: range.from,
                max: range.to,
                axisLabel: { hideOverlap: true },
            },
            yAxis: [
                {
                    type: "value",
                    name: "volts",
                    scale: true,
                    axisLabel: { formatter: (value: number) => value.toFixed(1) },
                },
                {
                    type: "value",
                    name: "°F",
                    scale: true,
                    splitLine: { show: false },
                },
            ],
            dataZoom: [
                { type: "inside", xAxisIndex: 0 },
                { type: "slider", xAxisIndex: 0, height: 24, bottom: 28 },
            ],
            series: [
                {
                    name: "min–max",
                    type: "line",
                    stack: "band",
                    data: times.map((t, i) => [t, bandBase[i]]),
                    lineStyle: { opacity: 0 },
                    areaStyle: { opacity: 0 },
                    symbol: "none",
                    silent: true,
                    // Keep the invisible baseline out of the legend and the
                    // tooltip; only the visible half is meaningful.
                    legendHoverLink: false,
                    z: 1,
                },
                {
                    name: "min–max",
                    type: "line",
                    stack: "band",
                    data: times.map((t, i) => [t, bandHeight[i]]),
                    lineStyle: { opacity: 0 },
                    areaStyle: { color: "#3b82f6", opacity: 0.25 },
                    symbol: "none",
                    silent: true,
                    z: 1,
                },
                {
                    name: "mean",
                    type: "line",
                    data: times.map((t, i) => [t, meanLine[i]]),
                    showSymbol: false,
                    lineStyle: { width: 1.5, color: "#1d4ed8" },
                    // Design §8.2: the raw tier can reach tens of thousands of
                    // points, where these two settings are the difference
                    // between smooth panning and a stutter.
                    large: true,
                    sampling: "lttb",
                    z: 3,
                    markArea:
                        unplaced.length > 0
                            ? {
                                  silent: true,
                                  itemStyle: {
                                      color: "#f59e0b",
                                      opacity: 0.16,
                                  },
                                  label: {
                                      show: true,
                                      position: "top",
                                      formatter: "unplaced",
                                      fontSize: 10,
                                      color: "#92400e",
                                  },
                                  data: unplaced.map((region) => [
                                      { xAxis: region.from },
                                      { xAxis: region.to },
                                  ]),
                              }
                            : undefined,
                    markLine:
                        events.length > 0
                            ? {
                                  silent: true,
                                  symbol: "none",
                                  lineStyle: { color: "#dc2626", width: 1 },
                                  data: events.map((event) => ({
                                      xAxis: event.at,
                                      label: { formatter: event.kind },
                                  })),
                              }
                            : undefined,
                },
                {
                    name: "temperature",
                    type: "line",
                    yAxisIndex: 1,
                    data: times.map((t, i) => [t, tempLine[i]]),
                    showSymbol: false,
                    lineStyle: { width: 1, color: "#059669", type: "dashed" },
                    // A gap in temperature is real information (some entries
                    // carry no reading) and must not be bridged.
                    connectNulls: false,
                    z: 2,
                },
            ],
        };
    }, [series, events, range, unplaced]);

    const onEvents = useMemo(
        () => ({
            dataZoom: (event: unknown) => {
                const zoom = event as {
                    batch?: { startValue?: number; endValue?: number }[];
                    startValue?: number;
                    endValue?: number;
                };
                const source = zoom.batch?.[0] ?? zoom;
                if (
                    typeof source.startValue !== "number" ||
                    typeof source.endValue !== "number"
                ) {
                    return;
                }
                const next = {
                    from: Math.round(source.startValue),
                    to: Math.round(source.endValue),
                };
                if (next.to <= next.from) return;
                pendingRange.current = next;
                onRangeChange(next);
            },
        }),
        [onRangeChange],
    );

    return (
        <div className="chart" aria-busy={loading ?? false}>
            <ReactEChartsCore
                echarts={echarts}
                option={option}
                notMerge
                lazyUpdate
                style={{ height: 460, width: "100%" }}
                onEvents={onEvents}
            />
            {loading ? <div className="chart-loading">loading…</div> : null}
        </div>
    );
}
